import { Layers, X } from 'lucide-react';

const DISC_COLORS = {
  mechanical: { text: 'text-red-500' },
  hvac: { text: 'text-red-500' },
  electrical: { text: 'text-yellow-500' },
  plumbing: { text: 'text-green-500' },
  'fire protection': { text: 'text-orange-500' },
  architectural: { text: 'text-blue-500' },
  structural: { text: 'text-purple-500' },
};

const DISC_CODE_NAMES = {
  A: 'Architectural', S: 'Structural', M: 'Mechanical', E: 'Electrical',
  P: 'Plumbing', FP: 'Fire Protection', C: 'Civil', L: 'Landscape',
  G: 'General', T: 'Telecom', FA: 'Fire Alarm',
};

const discLabel = (d) => {
  if (!d) return 'Unclassified';
  return DISC_CODE_NAMES[String(d).toUpperCase()] || d;
};

export default function FloorPlanSheetPicker({ sheets, currentMasterId, overlayIds = [], onSelect, onAddOverlay, onClose, onProcessDrawings }) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-card border border-border rounded-sm p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto text-left" onClick={e => e.stopPropagation()}
        data-testid="sheet-picker-modal">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-sm font-heading uppercase tracking-wider text-foreground flex items-center gap-2">
            <Layers className="w-4 h-4 text-primary" /> Select Sheet
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Set as master floor plan or add as a discipline overlay</p>
        {sheets.length === 0 ? (
          <div className="text-center py-8" data-testid="sheet-picker-empty">
            <p className="text-sm text-muted-foreground mb-1">No digitized sheets available.</p>
            <p className="text-xs text-muted-foreground mb-4">Process your uploaded drawings into sheets to use them as floor plans.</p>
            {onProcessDrawings && (
              <button onClick={() => { onClose?.(); onProcessDrawings(); }} data-testid="picker-process-drawings-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-heading uppercase tracking-wider bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 rounded-sm hover:bg-emerald-500/15 transition-colors">
                <Layers className="w-3.5 h-3.5" /> Go to Drawing Sets
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {sheets.map(s => {
              const isMaster = s.id === currentMasterId;
              const isOverlay = overlayIds.includes(s.id);
              return (
                <div key={s.id} className={`flex items-center gap-3 p-3 border rounded-sm transition-all ${
                  isMaster ? 'border-primary bg-primary/5' : isOverlay ? 'border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-950/10' : 'border-border hover:border-primary/30'
                }`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-heading text-foreground">{s.sheet_name || 'Untitled'}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {[
                        s.sheet_number || (s.page_number ? `Page ${s.page_number}` : null),
                        discLabel(s.discipline),
                        s.room_count ? `${s.room_count} rooms` : null,
                      ].filter(Boolean).join(' — ')}
                    </p>
                  </div>
                  {isMaster ? (
                    <span className="text-[9px] font-heading text-primary px-2 py-1 bg-primary/10 rounded-sm">MASTER</span>
                  ) : isOverlay ? (
                    <span className="text-[9px] font-heading text-purple-500 px-2 py-1 bg-purple-50 dark:bg-purple-950/20 rounded-sm">OVERLAY</span>
                  ) : (
                    <div className="flex gap-1.5">
                      <button onClick={() => onSelect(s.id)} data-testid={`set-master-${s.id}`}
                        className="text-[9px] font-heading px-2 py-1 bg-primary/10 text-primary border border-primary/30 rounded-sm hover:bg-primary/20">
                        Set Master
                      </button>
                      <button onClick={() => onAddOverlay(s.id)} data-testid={`add-overlay-${s.id}`}
                        className="text-[9px] font-heading px-2 py-1 bg-purple-50 dark:bg-purple-950/20 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-700 rounded-sm hover:bg-purple-100 dark:hover:bg-purple-900/30">
                        + Overlay
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 text-xs text-muted-foreground border border-border rounded-sm hover:bg-muted transition-colors">Close</button>
        </div>
      </div>
    </div>
  );
}
